import { ChatbotHistory } from "../shared/models/chatbot-history.model";
import { Metadata, PropertiesModel } from "../shared/models/metadata.model";
import { SearchOptions } from "../shared/models/search-options.model";
import { Site } from "../shared/models/site.model";
import { SourceItem } from "../shared/models/source-item.model";
import { LocalizedTerm } from "../shared/models/terms.model";
import { Theme } from "../shared/models/theme.model";
import { Callback, CssClassRule, InjectElementRule } from "./state-types";

export type ArrayState = {
    array?: Array<SourceItem>;
};

export type SiteState = {
    site?: Site | null;                      // current site
    theme?: Theme | null;                    // current theme
    terms?: Record<string, LocalizedTerm>;   // set of localizable terms
    sitePath?: string | null;                // path to the current site, as specified in the url
};

export type FileState = {
    metadata?: Metadata;              // Current metadata
    content?: string;                 // content of the current file, as plain html
    baselineContent?: string;         // content of the baseline file, as plain html
    path?: string;                    // path to the currently loaded file
};

export type BookState = {
    bookMetadata?: Metadata;             // Current metadata for the loaded book file
    bookContent?: string;                // content of the currently loaded book file
    bookPath?: string;                   // path to the currently loaded book file
    /** When true, `book` may be written to the URL; when false, book is display-only (e.g. site default). */
    bookSerializeToUrl?: boolean;
    /**
     * False while a client book-metadata fetch is in flight after `bookPath` changes.
     * Undefined/true means the last lookup has settled (including SSR).
     */
    bookLookupSettled?: boolean;
};

export type FilterState = {
    customProperties?: PropertiesModel;       // Custom property filters
    systemProperties?: PropertiesModel;       // System property filters
    displayProperties?: PropertiesModel;      // Display property filters
    defaultCustomProps?: PropertiesModel;     // Indicates which custom properties that use the default value
    defaultSystemProps?: PropertiesModel;     // Indicates which system properties that use the default value
    defaultSearchOptions?: Array<string>;     // Indicates which search options that use the default value
    validity?: string;                        // Validity filters, colon-separated values
    lang?: string;                            // Language filter targeting the validityInfo.langs calculated languages for each validity
    text?: string;                            // Text filter for word search
    semantic?: string;                        // Text filter for semantic search
    localSearch?: string;                     // Text filter for local search
    searchOptions?: SearchOptions;            // Search options, such as search order
    baseline?: string;                        // Baseline for displaying differences using validity
    organization?: string;                    // Organization filter
    configuration?: string;                   // Configuration filter
    contentType?: string;                     // Content type filter
    caseSensitiveSearch?: boolean;            // Case sensitive search filter
    wholeWordSearch?: boolean;                // Whole word search filter
};


export type InternalState = {
    contentType?: string;                   // Content type filter
    isCustomDomain?: boolean;               // Request-scoped flag for custom-domain link generation
    cssClassRules?: Array<CssClassRule>;     // Rule based manipulation of css classes for highlighting etc
    injectElementRules?: Array<InjectElementRule>; // Id based injection of elements (mainly for adding components to SVG content)
    callbacks?: Record<string, Callback>; // Callbacks that can be used to communicate from a descendant component to an ancestor
    isValidForm?: boolean;                   // Indicates if a form in the current state is valid
    storedItems?: Record<string, SourceItem>; // Customizable stores containing source items
    storedArrays?: Record<string, Array<SourceItem>>; // Customizable stores containing source arrays
    chatbotHistory?: ChatbotHistory;
};

export type State = SiteState | FileState | BookState | FilterState | InternalState;

export const reduceStateToFileState = (state: State): FileState => {
    const asFileState = state as FileState;
    return {
        metadata: asFileState.metadata,
        content: asFileState.content,
        baselineContent: asFileState.baselineContent,
        path: asFileState.path,
    }
}

export const reduceStateToBookState = (state: State): BookState => {
    const asBookState = state as BookState;
    return {
        bookMetadata: asBookState.bookMetadata,
        bookContent: asBookState.bookContent,
        bookPath: asBookState.bookPath,
        bookSerializeToUrl: asBookState.bookSerializeToUrl,
        bookLookupSettled: asBookState.bookLookupSettled,
    }
}

export const reduceStateToSiteState = (state: State): SiteState => {
    const asSiteState = state as SiteState;
    return {
        site: asSiteState.site,
        theme: asSiteState.theme,
        terms: asSiteState.terms,
        sitePath: asSiteState.sitePath,
    }
}

export const reduceStateToFilterState = (state: State): FilterState => {
    const asFilterState = state as FilterState;
    return {
        customProperties: asFilterState.customProperties,
        systemProperties: asFilterState.systemProperties,
        displayProperties: asFilterState.displayProperties,
        defaultCustomProps: asFilterState.defaultCustomProps,
        defaultSystemProps: asFilterState.defaultSystemProps,
        defaultSearchOptions: asFilterState.defaultSearchOptions,
        validity: asFilterState.validity,
        lang: asFilterState.lang,
        text: asFilterState.text,
        semantic: asFilterState.semantic,
        searchOptions: asFilterState.searchOptions,
        baseline: asFilterState.baseline,
        organization: asFilterState.organization,
        configuration: asFilterState.configuration,
        contentType: asFilterState.contentType,
        caseSensitiveSearch: asFilterState.caseSensitiveSearch,
        wholeWordSearch: asFilterState.wholeWordSearch,
        localSearch: asFilterState.localSearch,
    }
}

export const reduceStateToInternalState = (state: State): InternalState => {
    const asInternalState = state as InternalState;
    return {
        contentType: asInternalState.contentType,
        isCustomDomain: asInternalState.isCustomDomain,
        cssClassRules: asInternalState.cssClassRules,
        injectElementRules: asInternalState.injectElementRules,
        callbacks: asInternalState.callbacks,
        isValidForm: asInternalState.isValidForm,
        storedItems: asInternalState.storedItems,
        storedArrays: asInternalState.storedArrays,
    }
}
